import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Download, House, ShareNetwork } from 'phosphor-react';

const PWAInstallPrompt = () => {
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // スタンドアロンモード（既にインストール済み）かチェック
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true || 
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // iOSデバイスかチェック - より包括的な判定
    const iOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && 
                !window.MSStream;
    setIsIOS(iOS);

    // Androidデバイスかチェック
    const isAndroid = /Android/.test(navigator.userAgent);

    console.log('PWA Install Prompt Debug:', {
      standalone,
      iOS,
      isAndroid,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
      navigatorStandalone: window.navigator.standalone,
      dismissed: localStorage.getItem('pwa-install-dismissed')
    });

    // ?showPWAPrompt=true で強制表示（実機テスト用）
    const params = new URLSearchParams(window.location.search);
    if (params.get('showPWAPrompt') === 'true') {
      localStorage.removeItem('pwa-install-dismissed');
    }

    // 既にインストール済み、または促進バナーを閉じた履歴があれば表示しない
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (standalone) {
      console.log('PWA already installed, not showing prompt');
      return;
    }
    
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      if (dismissedDate > now) {
        console.log('PWA prompt dismissed until:', dismissedDate);
        return;
      } else {
        // 期限切れなので削除
        localStorage.removeItem('pwa-install-dismissed');
      }
    }

    // Androidの場合、beforeinstallpromptイベントをキャッチ
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event received:', e);
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOSの場合、3秒後に自動表示（Safari判定も追加）
    if (iOS && !standalone) {
      // Safariブラウザかもチェック
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      console.log('iOS Safari check:', { iOS, isSafari, standalone });
      
      if (isSafari || iOS) {
        console.log('iOS/Safari detected, showing prompt in 3 seconds');
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
      }
    }

    // Androidで5秒経ってもbeforeinstallpromptが発火しない場合の対処
    if (isAndroid && !iOS && !standalone) {
      const fallbackTimer = setTimeout(() => {
        if (!deferredPrompt) {
          console.log('No beforeinstallprompt event received, showing fallback prompt');
          setShowPrompt(true);
        }
      }, 5000);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        clearTimeout(fallbackTimer);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    if (!isIOS && deferredPrompt) {
      // Androidの場合
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.setItem('pwa-install-dismissed', 'true');
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // 7日間は表示しない
    const dismissedUntil = new Date();
    dismissedUntil.setDate(dismissedUntil.getDate() + 7);
    localStorage.setItem('pwa-install-dismissed', dismissedUntil.toISOString());
  };

  // ログイン、新規登録、チャットページでは表示しない
  const hiddenPaths = ['/login', '/register', '/chat'];
  const shouldHide = hiddenPaths.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // インストール済み、表示しない場合、非表示ページの場合
  if (isStandalone || !showPrompt || shouldHide) {
    return null;
  }

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 px-4 max-w-lg w-full"
      style={{
        bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        zIndex: 99
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* ヘッダー */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <House className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-base">ホーム画面に追加</h3>
                  <p className="text-xs text-white/90">いつでも簡単にアクセス</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="閉じる"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* コンテンツ */}
          <div className="p-4">
            {/* メリット */}
            <div className="space-y-2 mb-4">
              <div className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <p className="text-sm text-gray-700">アプリのようにワンタップで起動</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <p className="text-sm text-gray-700">全画面表示でより使いやすく</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <p className="text-sm text-gray-700">オフラインでも基本機能が利用可能</p>
              </div>
            </div>

            {/* インストール方法 */}
            {isIOS ? (
              <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">
                  iPhoneへの追加方法:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">1</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <p className="text-xs text-gray-700">下部の「共有」ボタンをタップ</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">2</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        <rect x="4" y="4" width="16" height="16" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth={1.5} />
                      </svg>
                      <p className="text-xs text-gray-700">「ホーム画面に追加」を選択</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">3</span>
                    </div>
                    <p className="text-xs text-gray-700">「追加」をタップで完了！</p>
                  </div>
                </div>
              </div>
            ) : deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-3 px-4 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>今すぐインストール</span>
              </button>
            ) : (
              <div className="bg-green-50 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">
                  Androidへの追加方法:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">1</span>
                    </div>
                    <p className="text-xs text-gray-700">Chromeのメニュー（︙）をタップ</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">2</span>
                    </div>
                    <p className="text-xs text-gray-700">「ホーム画面に追加」を選択</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">3</span>
                    </div>
                    <p className="text-xs text-gray-700">「追加」をタップで完了！</p>
                  </div>
                </div>
              </div>
            )}

            {/* あとでボタン */}
            <button
              onClick={handleDismiss}
              className="w-full mt-3 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              あとで
            </button>

            {/* デバッグ用：強制再表示ボタン（開発時のみ） */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => {
                  localStorage.removeItem('pwa-install-dismissed');
                  window.location.reload();
                }}
                className="w-full mt-2 text-red-500 text-xs hover:text-red-700 transition-colors"
              >
                [開発用] PWA設定をリセット
              </button>
            )}
          </div>
        </div>
      </div>
  );
};

export default PWAInstallPrompt;